import {Brand} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/brand.types';
import {FollowBrandsSection} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/order_success_page/v1/sections.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  BrandLogo,
  COLORS,
  FONT_SIZES,
  GenericIconName,
  HorizontalList,
  HorizontalListWrapper,
  Icon,
  SliderPaginationBase,
  Text,
  UnstyledButton,
  UnstyledLinkLinaria,
  desktopAndUpLinaria,
  mobileOnlyLinaria,
  tabletAndUpLinaria,
  useShowToast,
  useSlider,
} from '@aboutyou/ui-components';
import {useBrandToggleFollow} from '@aboutyou/user';
import {styled} from '@linaria/react';
import React, {startTransition, useState} from 'react';
import {useIsMissionsActive} from '../../../features/Coins/utils/useIsMissionsActive';
import {useSubmitMissionProgress} from '../../Coins/utils/useSubmitMissionProgress';

export const FollowBrands = (props: FollowBrandsSection) => {
  const {brands, headline} = props;
  const scrollWrapperRef = React.useRef<HTMLDivElement>(null);
  const toast = useShowToast();

  const {t} = useTranslation();

  const [followed, setFollowed] = useState<number[]>([]);

  const onToggle = (brandId: number) => {
    startTransition(() => {
      const newFollowed = followed.includes(brandId)
        ? followed.filter(id => id !== brandId)
        : [...followed, brandId];

      if (newFollowed.length === props.brands.length) {
        toast({
          type: 'SUCCESS',
          content: t('All brands saved'),
        });
      }
      setFollowed(newFollowed);
    });
  };

  const {next, prev, nextIsActive, prevIsActive, scrollToSlide} =
    useSlider(scrollWrapperRef);
  return (
    <FollowBrandsStreamWrapper data-testid="followBrandsContainer">
      <Container>
        <HeadlineWrapper>
          <Headline weight="mediumHeavy">{headline}</Headline>
          <SliderPagination
            prev={prev}
            next={next}
            prevIsActive={prevIsActive}
            nextIsActive={nextIsActive}
            aria-label={undefined}
            scrollToSlide={scrollToSlide}
          />
        </HeadlineWrapper>
        <ListContainer>
          <StyledHorizontalList ref={scrollWrapperRef}>
            {brands?.map(brand => (
              <BrandTileWrapper key={brand.id}>
                <BrandTileLink to={brand.path} aria-label={brand.name}>
                  <BrandContent>
                    <StyledBrandLogo
                      image={brand.blackLogoImage}
                      name={brand.name}
                      maxHeight="100%"
                      showTooltip={false}
                    />

                    <BrandFollowIcon brand={brand} onToggle={onToggle} />
                  </BrandContent>
                </BrandTileLink>
                <UnstyledLinkLinaria to={brand.path} aria-label={brand.name}>
                  <BrandName size="px12" weight="heavy" textAlignment="center">
                    {brand.name}
                  </BrandName>
                </UnstyledLinkLinaria>
              </BrandTileWrapper>
            ))}
          </StyledHorizontalList>
        </ListContainer>
      </Container>
    </FollowBrandsStreamWrapper>
  );
};

const BrandFollowIcon = (props: {
  brand: Brand;
  onToggle: (brandId: number) => void;
}) => {
  const {brand, onToggle} = props;
  const {t} = useTranslation();
  const isMissionActive = useIsMissionsActive();
  const submitLikeBrandMission = useSubmitMissionProgress({
    mission: 'likeBrandMission',
  });
  const {isFollowed, toggleFollow} = useBrandToggleFollow({
    brand,
    onFollowSuccess: () => {
      if (isMissionActive) {
        submitLikeBrandMission(brand.id);
      }
    },
  });

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggle(brand.id);
    toggleFollow();
  };

  return (
    <UnstyledButton
      onClick={handleClick}
      data-testid={
        `follow-brand-${brand.id}-` + (isFollowed ? 'unfollow' : 'follow')
      }
      aria-label={t('Follow brand {brandName}', {brandName: brand.name})}
      aria-pressed={isFollowed}
    >
      <StyledFollowIcon
        asset={
          isFollowed
            ? GenericIconName.AlertSuccessFill
            : GenericIconName.AddCircleFill
        }
        size="M"
      />
    </UnstyledButton>
  );
};

const SliderPagination = styled(SliderPaginationBase)`
  margin: 0;
  display: none;
  ${tabletAndUpLinaria(`
    display: block;
  `)}
`;

const HeadlineWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;

  ${tabletAndUpLinaria(`
   justify-content: space-between;
  `)}
`;

export const FollowBrandsStreamWrapper = styled.div`
  background-color: ${COLORS.White};
  padding: 2rem 0;
`;

const StyledFollowIcon = styled(Icon)`
  cursor: pointer;
  user-select: none;
`;

const Headline = styled(Text)`
  line-height: 1.44;

  font-size: ${FONT_SIZES.px20};

  text-align: center;

  ${desktopAndUpLinaria(`
    font-size: ${FONT_SIZES.px24};
    line-height: 1.167;
     `)}
`;

const StyledBrandLogo = styled(BrandLogo)`
  text-align: center;
`;

const StyledHorizontalList = styled(HorizontalList)<{
  $centered?: boolean;
}>`
  display: flex;

  ${HorizontalListWrapper} {
    gap: 0.5rem;
    overflow-y: hidden;

    padding: 0 1rem;

    // the following width property is needed to help mobile Safari to set the correct size on BrandTiles (ASFE-7100 / ASFE-7105)
    width: 100%;

    // since the width of this list is 100% on mobile, the following justify-content property
    // is needed to keep the brand tiles centered aligned when they fit the screen width
    justify-content: ${({$centered}) => ($centered ? 'center' : 'flex-start')};

    ${tabletAndUpLinaria(`
      width: auto;
      justify-content: unset;
      padding: 0;
    `)}

    ${mobileOnlyLinaria(`
      &::after {
        background: none;
      }
    `)}
  }
`;

const BrandTileWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ListContainer = styled.div`
  position: relative;
  display: flex;
  max-width: 100%;
  height: 100%;
  justify-content: center;
`;

const BrandTileLink = styled(UnstyledLinkLinaria)`
  border: 1px solid ${COLORS['Outline.Grey']};
  border-radius: 0.5rem;
  height: 4.625rem;
  padding: 1rem 1rem 1rem 1.5rem;
  width: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const BrandContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
  height: 100%;
`;

const BrandName = styled(Text)`
  margin-top: 0.38rem;
  text-transform: uppercase;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;
